import React from "react";
import classNames from "classnames";
import { useTranslation } from "react-i18next";

import {
  useRetrieveCurrentPlanzone,
  useRetrievePlanzoneSettings,
} from "@planzone/view-models";
import { UseCases } from "@planzone/core";
import { useModal } from "@root/client/modal";
import { RestrictedArea } from "@scenes/restrictedArea";
import { Safeguard } from "@scenes/safeguard";
import { LockIcon } from "@common/lockIcon";
import { DocumentIcon } from "@common/documentIcon";
import { UploadIcon } from "../creationMenu/uploadIcon";
import { CreateFolderIcon } from "../creationMenu/createFolderIcon";
import { Props } from "./types";
import styles from "./styles.scss";

function Placeholder({ folder }: Props) {
  const { t } = useTranslation();
  const { planzone } = useRetrieveCurrentPlanzone();
  const { settings } = useRetrievePlanzoneSettings(planzone);
  const { pushView } = useModal();

  return (
    <Safeguard
      useCases={[
        UseCases.uploadDocument,
        UseCases.createFolder,
        UseCases.createDocument,
      ]}
    >
      {({ canCreateFolder, canUploadDocument, canCreateDocument }) => (
        <RestrictedArea areas={["onlyOffice"]}>
          {({ canAccessOnlyOffice }) => {
            const canUseOnlyOffice =
              settings.canUseOnlyOffice && canAccessOnlyOffice;

            return (
              <div className={styles.placeholder}>
                <div className={styles.main}>
                  <h3 className={styles.title}>
                    {t("documentsPage.documentsPage.placeholder.title")}
                  </h3>
                  {(canUploadDocument ||
                    canCreateFolder ||
                    (canCreateDocument && canUseOnlyOffice)) && (
                    <h4 className={styles.subTitle}>
                      {t("documentsPage.documentsPage.placeholder.addElement")}
                    </h4>
                  )}
                  {(canUploadDocument || canCreateFolder) && (
                    <div className={styles.menu}>
                      {canCreateDocument && (
                        <React.Fragment>
                          <div
                            className={classNames(styles.menuItem, {
                              [styles.block]: !canUseOnlyOffice,
                            })}
                            onClick={() =>
                              canUseOnlyOffice &&
                              pushView({
                                key: UseCases.createDocument,
                                data: {
                                  documentType: ".docx",
                                  namespace: planzone.uuid,
                                  type: "planzone",
                                  projectName: planzone.name,
                                },
                              })
                            }
                          >
                            <div className={styles.fileIcon}>
                              {!canUseOnlyOffice ? (
                                <LockIcon className={styles.lockIcon} />
                              ) : (
                                <DocumentIcon mimeType={"application/msword"} />
                              )}
                            </div>
                            <p className={styles.menuText}>
                              {t(
                                "documentsPage.documentsPage.elementCreationMenu.createDocx",
                              )}
                            </p>
                          </div>
                          <div
                            className={classNames(styles.menuItem, {
                              [styles.block]: !canUseOnlyOffice,
                            })}
                            onClick={() =>
                              canUseOnlyOffice &&
                              pushView({
                                key: UseCases.createDocument,
                                data: {
                                  documentType: ".pptx",
                                  namespace: planzone.uuid,
                                  type: "planzone",
                                  projectName: planzone.name,
                                },
                              })
                            }
                          >
                            <div className={styles.fileIcon}>
                              {!canUseOnlyOffice ? (
                                <LockIcon className={styles.lockIcon} />
                              ) : (
                                <DocumentIcon
                                  mimeType={
                                    "application/vnd.openxmlformats-officedocument.presentationml.presentation"
                                  }
                                />
                              )}
                            </div>
                            <p className={styles.menuText}>
                              {t(
                                "documentsPage.documentsPage.elementCreationMenu.createSlide",
                              )}
                            </p>
                          </div>
                          <div
                            className={classNames(styles.menuItem, {
                              [styles.block]: !canUseOnlyOffice,
                            })}
                            onClick={() =>
                              canUseOnlyOffice &&
                              pushView({
                                key: UseCases.createDocument,
                                data: {
                                  documentType: ".xlsx",
                                  namespace: planzone.uuid,
                                  type: "planzone",
                                  projectName: planzone.name,
                                },
                              })
                            }
                          >
                            <div className={styles.fileIcon}>
                              {!canUseOnlyOffice ? (
                                <LockIcon className={styles.lockIcon} />
                              ) : (
                                <DocumentIcon mimeType={"image/xls"} />
                              )}
                            </div>
                            <p className={styles.menuText}>
                              {t(
                                "documentsPage.documentsPage.elementCreationMenu.createExcel",
                              )}
                            </p>
                          </div>
                        </React.Fragment>
                      )}
                      {canUploadDocument && (
                        <div
                          className={styles.menuItem}
                          onClick={() =>
                            pushView({
                              key: UseCases.uploadDocument,
                              data: {
                                namespace: planzone.uuid,
                                type: "planzone",
                                folder,
                              },
                            })
                          }
                        >
                          <div className={styles.iconBox}>
                            <UploadIcon className={styles.icon} />
                          </div>
                          <p className={styles.menuText}>
                            {t(
                              "documentsPage.documentsPage.elementCreationMenu.uploadDocument",
                            )}
                          </p>
                        </div>
                      )}
                      {canCreateFolder && (
                        <div
                          className={styles.menuItem}
                          onClick={() =>
                            pushView({
                              key: UseCases.createFolder,
                              data: {
                                namespace: planzone.uuid,
                                type: "planzone",
                                parentFolder: folder,
                                rootName: planzone.name,
                              },
                            })
                          }
                        >
                          <div className={styles.iconBox}>
                            <CreateFolderIcon className={styles.icon} />
                          </div>
                          <p className={styles.menuText}>
                            {t(
                              "documentsPage.documentsPage.elementCreationMenu.createFolder",
                            )}
                          </p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            );
          }}
        </RestrictedArea>
      )}
    </Safeguard>
  );
}

export { Placeholder };
